// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue';
import VueI18n from 'vue-i18n';
import moment from 'moment';
import Toasted from 'vue-toasted';
import VeeValidate, { Validator } from 'vee-validate';
import SvgIcon from 'vue-svgicon';
import ru from '@/lang/ru.json';
import en from '@/lang/en.json';
import uk from '@/lang/uk.json';
import cs from '@/lang/cz.json';
import pl from '@/lang/pl.json';
import pt from '@/lang/pt.json';
import api, { setToken } from '@/api';
import App from './App';
import Auth from './Auth';
import router from './router';
import './theme.scss';
import global from './plugin/global';
import eventBus from './plugin/eventBus';
import userInfo from './plugin/user';
import currencyPlugin from './plugin/currency';
import { b64DecodeUnicode, getLocale, getLang, getComponentLocale } from './utils/utils';
import './components/svg';

// eslint-disable-next-line
Vue.use(eventBus);
const lang = getLocale();
const APP = document.getElementById('app');
const AUTH = document.getElementById('auth');
const {
  spa: isSpa = '1',
  theme = 'unknow',
  auth: isAuth = '0',
  fullWidth,
  from,
  to,
  daysFromNow = '1',
  additionalSearch = 'hide',
  searchBack = '0',
  searchChanges = '0',
  searchCurrency = '1',
  sortType = 'asc',
  sortField = 'fullPrice',
  newTab = '0',
  defaultPoints = '0',
} = APP.dataset;

let position = 'out-of-flow';
let title = '';
let spaAuth = true;

if (isAuth === '1' && AUTH) {
  position = AUTH.dataset.position || 'out-of-flow';
  title = AUTH.dataset.title || '';
  spaAuth = AUTH.dataset.spa || true;
}

Vue.use(SvgIcon, {
  tagName: 'svg-icon',
  defaultWidth: '1.5rem',
  defaultHeight: '1.5rem',
  isStroke: true,
});

Vue.config.productionTip = false;
Vue.use(VueI18n);
Vue.use(Toasted, {
  position: 'bottom-center',
  duration: 2000,
});
moment.locale(getComponentLocale(lang));

// Ready translated locale messages
const messages = {
  ru,
  uk,
  en,
  cs,
  pl,
  pt,
};

// Create VueI18n instance with options
const i18n = new VueI18n({
  locale: lang, // set locale
  messages, // set locale messages
});

const birthDay = {
  getMessage(field, params, data) {
    return (data && data.message) || messages[lang].error.birthday;
  },
  validate(value) {
    return new Promise((resolve) => {
      let data = {
        valid: false,
        data: undefined,
      };

      if (value.length === 10) {
        const [day, month, year] = value.split('-');
        const convertValue = `${year}-${month}-${day}`;

        data.valid = moment(convertValue).isValid();
        data.data = undefined;
      } else {
        data = {
          valid: false,
          data: {
            message: messages[lang].error.birthday,
          },
        };
      }

      resolve(data);
    });
  },
};

const dictionary = {
  ru: {
    messages: {
      email: () => ru.error.email,
      required: () => ru.error.required,
      confirmed: () => ru.error.confirm,
      max: () => ru.error.max,
      min: () => ru.error.min,
    },
  },
  uk: {
    messages: {
      email: () => uk.error.email,
      required: () => uk.error.required,
      confirmed: () => uk.error.confirm,
      min: () => uk.error.min,
    },
  },
  en: {
    messages: {
      email: () => en.error.email,
      required: () => en.error.required,
      confirmed: () => en.error.confirm,
      min: () => en.error.min,
    },
  },
  cs: {
    messages: {
      email: () => cs.error.email,
      required: () => cs.error.required,
      confirmed: () => cs.error.confirm,
      min: () => cs.error.min,
    },
  },
  pl: {
    messages: {
      email: () => pl.error.email,
      required: () => pl.error.required,
      confirmed: () => pl.error.confirm,
      min: () => pl.error.min,
    },
  },
  pt: {
    messages: {
      email: () => pt.error.email,
      required: () => pt.error.required,
      confirmed: () => pt.error.confirm,
      min: () => pt.error.min,
    },
  },
};

Validator.localize(lang, dictionary[lang]);
Validator.extend('birthDay', birthDay);
Vue.use(VeeValidate);

Vue.filter('currency', (value, currency = '₴') => {
  if (!value) return '';

  const price = String(value).split('.');

  return `${price[0]}.${price[1] ? price[1].padEnd(2, '0') : '00'} ${currency}`;
});

function createVueApp() {
  /* eslint-disable no-new */
  new Vue({
    el: '#app',
    router,
    components: { App },
    template: '<App/>',
    i18n,
  });
}

function createVueAuth() {
  new Vue({
    el: '#auth',
    router,
    components: { Auth },
    template: '<Auth/>',
    i18n,
  });
}

api.AuthServer.login().then(async (res) => {
  setToken(res.token);
  const frameparams = await api.AuthServer.frameparams();

  Vue.use(
    global,
    frameparams,
    lang,
    isSpa,
    isAuth,
    fullWidth,
    position,
    title,
    theme,
    from,
    to,
    daysFromNow,
    additionalSearch,
    searchBack,
    searchChanges,
    searchCurrency,
    spaAuth,
    sortType,
    sortField,
    getLang(),
    newTab,
    defaultPoints,
  );

  const currency = await api.Content.currencyList();
  Vue.use(currencyPlugin, currency);

  const persistedUserData = localStorage.getItem('__octobus-user');
  if (persistedUserData) {
    try {
      const userId = JSON.parse(b64DecodeUnicode(persistedUserData)).id;
      const userRes = await api.Authentication.user(userId);
      if (userRes.error) {
        throw new Error(userRes.error.code);
      }
      Vue.use(userInfo, userRes);
    } catch (e) {
      localStorage.removeItem('__octobus-user');
    }
  }

  if (isAuth === '1') {
    createVueAuth();
  }
  createVueApp();
});



// WEBPACK FOOTER //
// ./src/main.js